import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { DashboardLayout } from "@/components/ui/dashboard-layout";
import { useMutation, useQuery } from "@tanstack/react-query";
import subscriptionApi from "@/lib/subscriptionApi";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { CreditCard, Zap, Check, RefreshCw } from "lucide-react";
import { SubscriptionPaywall } from "@/components/ui/subscription-paywall";

interface DisplaySubscription {
  planId: string;
  status:
    | "active"
    | "canceled"
    | "past_due"
    | "unpaid"
    | "incomplete"
    | "incomplete_expired"
    | "paused";
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  currentCredits: number;
  monthlyCredits: number;
}

export const Route = createFileRoute("/settings/subscription")({
  component: SubscriptionPage,
});

function SubscriptionPage() {
  const defaultSubscription: DisplaySubscription = {
    planId: "",
    status: "incomplete" as const,
    currentPeriodStart: "",
    currentPeriodEnd: "",
    currentCredits: 0,
    monthlyCredits: 0,
    cancelAtPeriodEnd: false,
  };

  // Fetch current subscription
  const subscriptionQuery = useQuery<DisplaySubscription>({
    queryKey: ["subscription"],
    queryFn: async () => {
      const response = await subscriptionApi.getCurrentSubscription();
      // Transform API response to match our interface
      return {
        planId: response.subscription.plan,
        status: response.subscription.status as DisplaySubscription["status"],
        currentPeriodStart: response.subscription.currentPeriodStart,
        currentPeriodEnd: response.subscription.currentPeriodEnd,
        cancelAtPeriodEnd: response.subscription.cancelAtPeriodEnd,
        currentCredits: response.subscription.currentCredits,
        monthlyCredits: response.subscription.monthlyCredits,
      };
    },
  });

  // Fetch subscription features
  const featuresQuery = useQuery({
    queryKey: ["subscriptionFeatures"],
    queryFn: () => subscriptionApi.getSubscriptionFeatures(),
  });

  // Fetch available plans
  const plansQuery = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: () => subscriptionApi.getPlans(),
  });

  // Fetch credit packs
  // const creditPacksQuery = useQuery({
  //   queryKey: ["creditPacks"],
  //   queryFn: () => subscriptionApi.getCreditPacks(),
  // });

  // Fetch credit history
  const creditsQuery = useQuery({
    queryKey: ["subscriptionCredits"],
    queryFn: () => subscriptionApi.getCredits({ limit: 10 }),
  });

  // Cancel plan mutation
  // const cancelPlanMutation = useMutation({
  //   mutationFn: () => subscriptionApi.cancelPlan(true),
  //   onSuccess: (data) => {
  //     toast.success("Subscription cancelled", {
  //       description: data.message,
  //     });
  //     // Refresh subscription data
  //     subscriptionQuery.refetch();
  //   },
  //   onError: (error) => {
  //     toast.error("Failed to cancel subscription", {
  //       description:
  //         error instanceof Error ? error.message : "Please try again later",
  //     });
  //   },
  // });

  // Manage payment mutation
  const managePaymentMutation = useMutation({
    mutationFn: () => subscriptionApi.createPortalSession(window.location.href),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast.error("Failed to access payment portal", {
        description:
          error instanceof Error ? error.message : "Please try again later",
      });
    },
  });

  const subscription = subscriptionQuery.data || defaultSubscription;

  // Get current plan details
  const currentPlan = plansQuery.data?.find(
    (plan) => plan.id === subscription.planId,
  );

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Helper function to get status badge style
  const getStatusBadgeStyle = (status: DisplaySubscription["status"]) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-600";
      case "canceled":
        return "bg-red-50 text-red-600";
      case "past_due":
        return "bg-yellow-50 text-yellow-600";
      case "unpaid":
        return "bg-red-50 text-red-600";
      case "incomplete":
        return "bg-blue-50 text-blue-600";
      case "incomplete_expired":
        return "bg-gray-50 text-gray-600";
      case "paused":
        return "bg-purple-50 text-purple-600";
      default:
        return "bg-gray-50 text-gray-600";
    }
  };

  // Helper function to get human-readable status text
  const getStatusText = (status: DisplaySubscription["status"]) => {
    switch (status) {
      case "active":
        return "Active";
      case "canceled":
        return "Canceled";
      case "past_due":
        return "Past Due";
      case "unpaid":
        return "Unpaid";
      case "incomplete":
        return "Payment Pending";
      case "incomplete_expired":
        return "Payment Failed";
      case "paused":
        return "Paused";
      default:
        return status;
    }
  };

  // Check if subscription needs attention
  const needsPaywall = (status: DisplaySubscription["status"]) => {
    return ["past_due", "unpaid", "incomplete", "incomplete_expired"].includes(
      status,
    );
  };

  // Early return with paywall if subscription needs attention
  if (needsPaywall(subscription.status)) {
    return (
      <DashboardLayout
        title="Subscription & Usage"
        activePath="/settings/subscription"
      >
        <SubscriptionPaywall
          status={subscription.status}
          onManagePayment={() => managePaymentMutation.mutate()}
          planName={currentPlan?.name}
          monthlyCredits={subscription.monthlyCredits}
        />
        {/* Show the regular content behind the paywall */}
        <div className="opacity-20 pointer-events-none">
          <div className="p-6">
            <div className="mx-auto max-w-6xl space-y-10">
              {/* Current Plan & Usage */}
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Current Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {subscriptionQuery.isLoading ? (
                      <div className="animate-pulse space-y-2">
                        <div className="h-6 bg-muted rounded w-1/2"></div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-2xl font-bold">
                          {currentPlan?.name || subscription.planId || "Free"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {featuresQuery.data?.maxConnections} connections
                          allowed
                        </div>
                        <div className="mt-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeStyle(subscription.status)}`}
                          >
                            {getStatusText(subscription.status)}
                          </span>
                          {subscription.status === "active" &&
                            currentPlan?.price && (
                              <span className="ml-2 text-sm text-muted-foreground">
                                {formatCurrency(currentPlan.price)}/mo
                              </span>
                            )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => managePaymentMutation.mutate()}
                      disabled={
                        managePaymentMutation.isPending ||
                        subscription.status !== "active"
                      }
                      className="flex-1"
                    >
                      {managePaymentMutation.isPending
                        ? "Loading..."
                        : "Manage Subscription"}
                    </Button>
                    {/* <Button
                      onClick={() => managePaymentMutation.mutate()}
                      disabled={managePaymentMutation.isPending}
                      className="flex-1"
                    >
                      {managePaymentMutation.isPending
                        ? "Loading..."
                        : "Manage Payment"}
                    </Button> */}
                  </CardFooter>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      Available Credits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {creditsQuery.isLoading ? (
                      <div className="animate-pulse space-y-2">
                        <div className="h-6 bg-muted rounded w-1/2"></div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-2xl font-bold">
                          {creditsQuery.data?.balance.toLocaleString()} credits
                        </div>
                        <Progress
                          value={
                            (((subscription.monthlyCredits || 100) -
                              (creditsQuery.data?.balance || 0)) /
                              (subscription.monthlyCredits || 100)) *
                            100
                          }
                          className="h-2 bg-blue-100"
                        />
                        <div className="text-sm text-muted-foreground">
                          {subscription.monthlyCredits.toLocaleString()} monthly
                          limit
                        </div>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      onClick={() => managePaymentMutation.mutate()}
                    >
                      Buy Credits
                    </Button>
                  </CardFooter>
                </Card>

                {/* <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Credit Packs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {creditPacksQuery.isLoading ? (
                      <div className="animate-pulse space-y-2">
                        <div className="h-6 bg-muted rounded w-1/2"></div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {creditPacksQuery.data?.creditPacks
                          .slice(0, 2)
                          .map((pack) => (
                            <div
                              key={pack.id}
                              className="flex justify-between items-center p-2 rounded bg-blue-50"
                            >
                              <span className="text-blue-700">{pack.name}</span>
                              <span className="font-medium text-blue-800">
                                {formatCurrency(pack.price)}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => managePaymentMutation.mutate()}
                    >
                      View All Packs
                    </Button>
                  </CardFooter>
                </Card> */}
              </div>

              {/* Subscription Details */}
              <div className="rounded-lg border p-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <h5 className="text-sm font-medium">Billing Period</h5>
                    <p className="mt-1 text-sm">
                      {formatDate(subscription.currentPeriodStart)} -{" "}
                      {formatDate(subscription.currentPeriodEnd)}
                    </p>
                  </div>

                  <div>
                    <h5 className="text-sm font-medium">Features</h5>
                    <ul className="mt-1 space-y-1">
                      {featuresQuery.data?.allowedSourceTypes.map(
                        (source: string) => (
                          <li
                            key={source}
                            className="text-sm flex items-center gap-2"
                          >
                            <Check className="h-4 w-4 text-blue-600" />
                            {source.charAt(0).toUpperCase() +
                              source.slice(1)}{" "}
                            Integration
                          </li>
                        ),
                      )}
                      <li className="text-sm flex items-center gap-2">
                        <Check className="h-4 w-4 text-blue-600" />
                        {subscription.monthlyCredits.toLocaleString()} Credits
                        per Month
                      </li>
                      {featuresQuery.data?.advancedAnalytics && (
                        <li className="text-sm flex items-center gap-2">
                          <Check className="h-4 w-4 text-blue-600" />
                          Advanced Analytics
                        </li>
                      )}
                      {featuresQuery.data?.prioritySupport && (
                        <li className="text-sm flex items-center gap-2">
                          <Check className="h-4 w-4 text-blue-600" />
                          Priority Support
                        </li>
                      )}
                    </ul>
                  </div>

                  {subscription.cancelAtPeriodEnd && (
                    <div className="col-span-2">
                      <p className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                        Your subscription will be cancelled at the end of the
                        current billing period.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Credit History */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium">Credit History</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => creditsQuery.refetch()}
                    disabled={creditsQuery.isRefetching}
                  >
                    <RefreshCw
                      className={`h-4 w-4 mr-2 ${creditsQuery.isRefetching ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </Button>
                </div>
                <div className="rounded-lg border">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Description
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-medium">
                            Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditsQuery.isLoading
                          ? Array(5)
                              .fill(0)
                              .map((_, i) => (
                                <tr key={i} className="border-b">
                                  <td className="px-4 py-3">
                                    <div className="h-4 bg-muted rounded w-24"></div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="h-4 bg-muted rounded w-48"></div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="h-4 bg-muted rounded w-16"></div>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="h-4 bg-muted rounded w-16 ml-auto"></div>
                                  </td>
                                </tr>
                              ))
                          : creditsQuery.data?.history.map((transaction) => (
                              <tr key={transaction.id} className="border-b">
                                <td className="px-4 py-3 text-sm">
                                  {formatDate(transaction.createdAt)}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  {transaction.description}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span
                                    className={
                                      transaction.amount >= 0
                                        ? "text-green-600 font-medium"
                                        : "text-red-600 font-medium"
                                    }
                                  >
                                    {transaction.amount >= 0 ? "+" : ""}
                                    {transaction.amount.toLocaleString()}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-right font-medium">
                                  {transaction.balance.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Subscription & Usage"
      activePath="/settings/subscription"
    >
      <div className="p-6">
        <div className="mx-auto max-w-6xl space-y-10">
          {/* Current Plan & Usage */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Current Plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                {subscriptionQuery.isLoading ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-6 bg-muted rounded w-1/2"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-2xl font-bold">
                      {currentPlan?.name || subscription.planId || "Free"}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {featuresQuery.data?.maxConnections} connections allowed
                    </div>
                    <div className="mt-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeStyle(subscription.status)}`}
                      >
                        {getStatusText(subscription.status)}
                      </span>
                      {subscription.status === "active" &&
                        currentPlan?.price && (
                          <span className="ml-2 text-sm text-muted-foreground">
                            {formatCurrency(currentPlan.price)}/mo
                          </span>
                        )}
                    </div>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => managePaymentMutation.mutate()}
                  disabled={
                    managePaymentMutation.isPending ||
                    subscription.status !== "active"
                  }
                  className="flex-1"
                >
                  {managePaymentMutation.isPending
                    ? "Loading..."
                    : "Manage Subscription"}
                </Button>
                {/* <Button
                  onClick={() => managePaymentMutation.mutate()}
                  disabled={managePaymentMutation.isPending}
                  className="flex-1"
                >
                  {managePaymentMutation.isPending
                    ? "Loading..."
                    : "Manage Payment"}
                </Button> */}
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Available Credits
                </CardTitle>
              </CardHeader>
              <CardContent>
                {creditsQuery.isLoading ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-6 bg-muted rounded w-1/2"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-2xl font-bold">
                      {creditsQuery.data?.balance.toLocaleString()} credits
                    </div>
                    <Progress
                      value={
                        (((subscription.monthlyCredits || 100) -
                          (creditsQuery.data?.balance || 0)) /
                          (subscription.monthlyCredits || 100)) *
                        100
                      }
                      className="h-2 bg-blue-100"
                    />
                    <div className="text-sm text-muted-foreground">
                      {subscription.monthlyCredits.toLocaleString()} monthly
                      limit
                    </div>
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full"
                  onClick={() => managePaymentMutation.mutate()}
                >
                  Buy Credits
                </Button>
              </CardFooter>
            </Card>

            {/* <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Credit Packs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {creditPacksQuery.isLoading ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-6 bg-muted rounded w-1/2"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {creditPacksQuery.data?.creditPacks
                      .slice(0, 2)
                      .map((pack) => (
                        <div
                          key={pack.id}
                          className="flex justify-between items-center p-2 rounded bg-blue-50"
                        >
                          <span className="text-blue-700">{pack.name}</span>
                          <span className="font-medium text-blue-800">
                            {formatCurrency(pack.price)}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => managePaymentMutation.mutate()}
                >
                  View All Packs
                </Button>
              </CardFooter>
            </Card> */}
          </div>

          {/* Subscription Details */}
          <div className="rounded-lg border p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <h5 className="text-sm font-medium">Billing Period</h5>
                <p className="mt-1 text-sm">
                  {formatDate(subscription.currentPeriodStart)} -{" "}
                  {formatDate(subscription.currentPeriodEnd)}
                </p>
              </div>

              <div>
                <h5 className="text-sm font-medium">Features</h5>
                <ul className="mt-1 space-y-1">
                  {featuresQuery.data?.allowedSourceTypes.map(
                    (source: string) => (
                      <li
                        key={source}
                        className="text-sm flex items-center gap-2"
                      >
                        <Check className="h-4 w-4 text-blue-600" />
                        {source.charAt(0).toUpperCase() + source.slice(1)}{" "}
                        Integration
                      </li>
                    ),
                  )}
                  <li className="text-sm flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-600" />
                    {subscription.monthlyCredits.toLocaleString()} Credits per
                    Month
                  </li>
                  {featuresQuery.data?.advancedAnalytics && (
                    <li className="text-sm flex items-center gap-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      Advanced Analytics
                    </li>
                  )}
                  {featuresQuery.data?.prioritySupport && (
                    <li className="text-sm flex items-center gap-2">
                      <Check className="h-4 w-4 text-blue-600" />
                      Priority Support
                    </li>
                  )}
                </ul>
              </div>

              {subscription.cancelAtPeriodEnd && (
                <div className="col-span-2">
                  <p className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                    Your subscription will be cancelled at the end of the
                    current billing period.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Credit History */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">Credit History</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => creditsQuery.refetch()}
                disabled={creditsQuery.isRefetching}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${creditsQuery.isRefetching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </div>
            <div className="rounded-lg border">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium">
                        Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditsQuery.isLoading
                      ? Array(5)
                          .fill(0)
                          .map((_, i) => (
                            <tr key={i} className="border-b">
                              <td className="px-4 py-3">
                                <div className="h-4 bg-muted rounded w-24"></div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="h-4 bg-muted rounded w-48"></div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="h-4 bg-muted rounded w-16"></div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="h-4 bg-muted rounded w-16 ml-auto"></div>
                              </td>
                            </tr>
                          ))
                      : creditsQuery.data?.history.map((transaction) => (
                          <tr key={transaction.id} className="border-b">
                            <td className="px-4 py-3 text-sm">
                              {formatDate(transaction.createdAt)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {transaction.description}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={
                                  transaction.amount >= 0
                                    ? "text-green-600 font-medium"
                                    : "text-red-600 font-medium"
                                }
                              >
                                {transaction.amount >= 0 ? "+" : ""}
                                {transaction.amount.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              {transaction.balance.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default SubscriptionPage;
